/**
 * Synchronous shape assertion for protected domain configs.
 *
 * Called at module-load time from each protected config file. A bogus shape
 * throws on import — so a coding agent introducing a regression sees the dev
 * server crash, `npm run build` fail, and Vitest fail to even load the file.
 * No CI, no pre-commit hook, no convention to remember.
 *
 * Replaces the old `source: 'input'` discriminator entirely. With `primary`
 * being a singular slot and `followUp` an array, "two primaries" is
 * structurally unrepresentable — the assert just checks the slot is filled
 * with a well-formed field, and the array is well-formed.
 */

const KNOWN_FIELD_TYPES = new Set([
  'string',
  'text',
  'number',
  'boolean',
  'multiChoice',
  'userId',
  'timestamp',
  'storageRef',
]);

function assertField(field, where) {
  if (!field || typeof field !== 'object') {
    throw new Error(`[domain shape] ${where}: expected field object, got ${typeof field}`);
  }
  if (typeof field.id !== 'string' || !field.id) {
    throw new Error(`[domain shape] ${where}: field is missing string id`);
  }
  if (typeof field.label !== 'string' || !field.label) {
    throw new Error(`[domain shape] ${where} (${field.id}): field is missing string label`);
  }
  if (typeof field.type !== 'string' || !KNOWN_FIELD_TYPES.has(field.type)) {
    throw new Error(
      `[domain shape] ${where} (${field.id}): unknown type "${field.type}". ` +
      `Allowed: ${[...KNOWN_FIELD_TYPES].join(', ')}`
    );
  }
  if (field.type === 'multiChoice') {
    if (!Array.isArray(field.options) || field.options.length === 0) {
      throw new Error(`[domain shape] ${where} (${field.id}): multiChoice requires non-empty options`);
    }
  }
  if ('source' in field) {
    throw new Error(
      `[domain shape] ${where} (${field.id}): obsolete \`source\` property. ` +
      `The discriminator was removed; put primary inputs in \`primary\`, ` +
      `secondary inputs in \`followUp\`. Engine-filled and workflow fields ` +
      `should not be in the config at all.`
    );
  }
}

/**
 * Non-throwing validation for a single user-authored field, for the builder's
 * write path. Returns an error string when the field is malformed, or null when
 * it's well-formed. Same rules as `assertField` (the import-time crash guard),
 * but surfaced as a toast instead of bringing the app down.
 *
 * @param {object} field
 * @param {string} [where] - label for the error message
 * @returns {string|null}
 */
export function validateUserField(field, where = 'field') {
  try {
    assertField(field, where);
    return null;
  } catch (err) {
    return err.message;
  }
}

export function assertDomainShape(config) {
  if (!config || typeof config !== 'object') {
    throw new Error('[domain shape] expected a config object');
  }
  const name = config.name || '<unnamed>';
  if (typeof config.name !== 'string' || !config.name) {
    throw new Error('[domain shape] config is missing string name');
  }
  if ('fields' in config) {
    throw new Error(
      `[domain shape] ${name}: obsolete \`fields\` array. ` +
      `Declare \`primary\` (single field) and \`followUp\` (array) instead. ` +
      `Engine-filled fields (recordedBy, occurredAt, state) belong nowhere ` +
      `in the config — the engine writes them.`
    );
  }
  if (!('primary' in config)) {
    throw new Error(
      `[domain shape] ${name}: missing \`primary\` — every domain must declare ` +
      `the singular headline field (the Quick Issue column).`
    );
  }
  assertField(config.primary, `${name}.primary`);
  if ('followUp' in config) {
    if (!Array.isArray(config.followUp)) {
      throw new Error(`[domain shape] ${name}.followUp: expected array`);
    }
    const seenIds = new Set([config.primary.id]);
    config.followUp.forEach((f, i) => {
      assertField(f, `${name}.followUp[${i}]`);
      if (seenIds.has(f.id)) {
        throw new Error(`[domain shape] ${name}: duplicate field id "${f.id}"`);
      }
      seenIds.add(f.id);
    });
  }
  return config;
}
